import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, PartyPopper, Calendar, Sparkles, Tag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { celebrationService } from '@/lib/api/celebration.service';
import { SplitScreenLayout } from '@/components/layout/SplitScreenLayout';
import { PageContainer } from '@/components/layout/PageContainer';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { getEmojiForType, getPublicTypes, getSubtypeLabel } from '@/config/celebrationRegistry';
import { formatCelebrationDates, getRelativeDateLabel } from '@/utils/dateFormat';
import type { Celebration } from '@/types/celebration.types';

const celebrationEmoji = (type: string): string => getEmojiForType(type);

export const CelebrationList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['celebrations', 'common']);
  const [celebrations, setCelebrations] = useState<Celebration[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');

  const loadCelebrations = useCallback(async () => {
    try {
      setLoading(true);
      const data = await celebrationService.getCelebrations();
      setCelebrations(data);
    } catch (error) {
      console.error('Error loading celebrations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCelebrations();
  }, [loadCelebrations]);

  const filteredCelebrations = celebrations.filter(celebration => {
    const matchesSearch = celebration.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         celebration.description?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || celebration.celebration_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const handleConversationEventCreated = (eventData: any) => {
    loadCelebrations();
    if (eventData.id) navigate(`/celebrations/${eventData.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Sparkles className="h-5 w-5 animate-pulse" />
          <span>{t('common:status.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <SplitScreenLayout onEventCreated={handleConversationEventCreated}>
      <PageContainer width="default" className="space-y-4">
        <AppBreadcrumbs />
        {/* Compact Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <PartyPopper className="h-5 w-5 text-primary" />
            <h1 className="text-lg font-bold">{t('celebrations:title')}</h1>
            <Badge variant="secondary" size="sm">{celebrations.length}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" onClick={() => navigate('/celebrations/new')}>
              <Plus className="h-3 w-3 mr-1" />
              {t('common:actions.new')}
            </Button>
          </div>
        </div>

        {/* Compact Filters */}
        <div className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={t('common:actions.search') + '...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
              compact
            />
          </div>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            aria-label="Filter celebrations by type"
            title="Filter celebrations by type"
            className="h-7 px-2 text-sm rounded-md border border-input bg-background"
          >
            <option value="all">{t('common:actions.all')}</option>
            {getPublicTypes().map(({ key, label, emoji }) => (
              <option key={key} value={key}>{emoji} {label}</option>
            ))}
          </select>
        </div>

        {/* Celebrations List */}
        {filteredCelebrations.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border">
            <div className="text-4xl mb-3">🎉</div>
            <h3 className="font-semibold text-foreground">{t('common:empty.noResults')}</h3>
            <p className="text-sm text-muted-foreground mt-1">
              {searchTerm || filterType !== 'all' 
                ? t('celebrations:empty')
                : t('celebrations:actions.createNew')
              }
            </p>
            {!searchTerm && filterType === 'all' && (
              <div className="mt-4 flex items-center justify-center gap-2">
                <Button size="sm" onClick={() => navigate('/celebrations/new')}>
                  <Plus className="h-3 w-3 mr-1" />
                  {t('celebrations:actions.createNew')}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {filteredCelebrations.map((celebration) => {
              const emoji = celebrationEmoji(celebration.celebration_type);
              const relativeDate = celebration.start_date ? getRelativeDateLabel(celebration.start_date) : null;
              
              return (
                <div
                  key={celebration.id}
                  onClick={() => navigate(`/celebrations/${celebration.id}`)}
                  className="bg-white rounded-lg border p-3 hover:border-primary/50 hover:shadow-sm transition-all cursor-pointer group"
                >
                  <div className="flex items-start gap-3">
                    {/* Emoji */}
                    <div className="text-2xl flex-shrink-0">{emoji}</div>
                    
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-sm truncate group-hover:text-primary transition-colors">
                          {celebration.title}
                        </h3>
                        <Badge variant={celebration.celebration_type as any} size="sm" className="capitalize">
                          {celebration.celebration_type}
                        </Badge>
                        {celebration.created_via === 'quick_create' && (
                          <Badge variant="secondary" size="sm" className="bg-amber-100 text-amber-700 border-amber-200">
                            ⚡ Quick
                          </Badge>
                        )}
                        {celebration.celebration_subtype && (
                          <Badge variant="outline" size="sm" className="text-[10px]">
                            {getSubtypeLabel(celebration.celebration_type, celebration.celebration_subtype)}
                          </Badge>
                        )}
                        {relativeDate && (relativeDate === 'Today' || relativeDate === 'Tomorrow') && (
                          <Badge variant="fun" size="sm">{relativeDate}</Badge>
                        )}
                      </div>
                      
                      {celebration.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                          {celebration.description}
                        </p>
                      )}
                      
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                        {celebration.start_date && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatCelebrationDates(celebration.start_date, celebration.end_date)}
                          </span>
                        )}
                        {celebration.budget && (
                          <span className="inline-flex items-center gap-1">
                            <span className="font-bold text-primary">$</span>
                            {celebration.budget.toLocaleString()}
                          </span>
                        )}
                      </div>

                      {/* Tag badges */}
                      {celebration.tags_snapshot && Object.keys(celebration.tags_snapshot).length > 0 && (
                        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                          <Tag className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                          {Object.values(celebration.tags_snapshot).flat().slice(0, 5).map((tag) => (
                            <span
                              key={tag}
                              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] bg-primary/5 text-primary/80 border border-primary/10"
                            >
                              {tag}
                            </span>
                          ))}
                          {Object.values(celebration.tags_snapshot).flat().length > 5 && (
                            <span className="text-[10px] text-muted-foreground">
                              +{Object.values(celebration.tags_snapshot).flat().length - 5}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Arrow indicator on hover */}
                    <div className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity">
                      →
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </PageContainer>
    </SplitScreenLayout>
  );
};
